import React, { createContext, useContext, useReducer, useEffect } from 'react';
import apiService from '../services/api';

const AuthContext = createContext();

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    default:
      return state;
  }
};

const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  loading: true,
  error: null,
};

// Helper function to check if token is valid (not expired)
const isTokenValid = (token) => {
  if (!token) return false;
  
  try {
    // Decode the JWT token to check expiration
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp > currentTime;
  } catch (error) {
    console.error('Error parsing token:', error);
    return false;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    // Check if user is logged in on app start
    const token = localStorage.getItem('token');

    if (token && isTokenValid(token)) {
      verifyToken();
    } else {
      // Clear invalid token
      if (token) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
      }
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // The API client emits this when a token refresh fails (session truly
  // expired). Drop the user to logged-out so the UI stops showing a broken
  // "logged in but every call 401s" state.
  useEffect(() => {
    const handleForcedLogout = () => dispatch({ type: 'LOGOUT' });
    window.addEventListener('auth:logout', handleForcedLogout);
    return () => window.removeEventListener('auth:logout', handleForcedLogout);
  }, []);

  const login = async (email, password) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const response = await apiService.login(email, password);
      
      // Transform user data to include name field
      const transformedUser = {
        ...response.user,
        name: `${response.user.first_name} ${response.user.last_name}`.trim(),
      };
      
      // Store tokens and user data
      localStorage.setItem('token', response.access);
      localStorage.setItem('refreshToken', response.refresh);
      localStorage.setItem('user', JSON.stringify(transformedUser));
      
      dispatch({
        type: 'LOGIN',
        payload: {
          user: transformedUser,
          token: response.access,
        },
      });
      
      return { success: true };
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, error: error.message };
    }
  };

  // Social sign-in: `provider` is 'google'|'facebook'|'apple', `token` the
  // provider-issued token. The backend verifies it and returns our JWT + user.
  const socialLogin = async (provider, token) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      const response = await apiService.socialAuth(provider, token);
      const transformedUser = {
        ...response.user,
        name: `${response.user.first_name} ${response.user.last_name}`.trim(),
      };

      localStorage.setItem('token', response.access);
      localStorage.setItem('refreshToken', response.refresh);
      localStorage.setItem('user', JSON.stringify(transformedUser));

      dispatch({
        type: 'LOGIN',
        payload: { user: transformedUser, token: response.access },
      });
      return { success: true };
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, error: error.message };
    }
  };

  const signup = async (firstName, lastName, email, password, confirmPassword, userType) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const response = await apiService.signup(email, password, firstName, lastName, userType, confirmPassword);
      
      // Transform user data to include name field
      const transformedUser = {
        ...response.user,
        name: `${response.user.first_name} ${response.user.last_name}`.trim(),
      };
      
      // Store tokens and user data
      localStorage.setItem('token', response.access);
      localStorage.setItem('refreshToken', response.refresh);
      localStorage.setItem('user', JSON.stringify(transformedUser));
      
      dispatch({
        type: 'LOGIN',
        payload: {
          user: transformedUser,
          token: response.access,
        },
      });
      
      return { success: true };
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      // Only attempt logout if we have a valid token
      const token = localStorage.getItem('token');
      if (token && isTokenValid(token)) {
        await apiService.logout();
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      dispatch({ type: 'LOGOUT' });
    }
  };

  const verifyToken = async () => {
    const token = localStorage.getItem('token');
    
    // Don't make API call if no token or invalid token
    if (!token || !isTokenValid(token)) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      dispatch({ type: 'LOGOUT' });
      return;
    }

    try {
      const response = await apiService.getCurrentUser();
      
      // Transform user data to include name field
      const transformedUser = {
        ...response,
        name: `${response.first_name} ${response.last_name}`.trim(),
      };
      
      dispatch({
        type: 'LOGIN',
        payload: {
          user: transformedUser,
          token: token,
        },
      });
    } catch (error) {
      // Handle authentication errors
      if (error.message === 'Authentication required' || error.message.includes('401')) {
        // Token is invalid, clear storage
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        dispatch({ type: 'LOGOUT' });
      } else {
        // Other errors, just log them
        console.error('Error verifying token:', error);
      }
    }
  };

  const value = {
    ...state,
    login,
    socialLogin,
    signup,
    logout,
    verifyToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 